import { useState } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { Layers, Monitor, Network, Brain, Database, Plug, ShieldCheck, UserCheck, FileSearch, Eye, ArrowRight } from 'lucide-react';

const LAYER_DATA = [
  { icon: Monitor, name: 'Interface Layer', color: 'from-blue-400/20 to-blue-400/5', accent: 'text-blue-400', border: 'border-blue-400/20',
    what: 'Allows interaction through dashboards, portals, messaging, booking flows, and operational surfaces.',
    why: 'Without an interface layer, there is no structured way to interact with the workforce system.' },
  { icon: Network, name: 'Orchestration Layer', color: 'from-aurocle-gold/20 to-aurocle-gold/5', accent: 'text-aurocle-gold', border: 'border-aurocle-gold/20',
    what: 'Routes work, decides which agent handles what, determines sequencing, prioritization, and workflow logic.',
    why: 'Without orchestration, agents operate in isolation — no coordination, no intelligent task routing.' },
  { icon: Brain, name: 'Specialist Agent Layer', color: 'from-emerald-400/20 to-emerald-400/5', accent: 'text-emerald-400', border: 'border-emerald-400/20',
    what: 'Contains Executive, Sales, Marketing, Operations, Finance, Legal, Research, Service, Procurement, and Private Office Agents.',
    why: 'Specialist roles ensure each domain receives deep, structured support — not generic responses.' },
  { icon: Database, name: 'Memory Layer', color: 'from-purple-400/20 to-purple-400/5', accent: 'text-purple-400', border: 'border-purple-400/20',
    what: 'Preserves business context, dossier history, organizational preferences, prior actions, and relational continuity.',
    why: 'Without memory, every interaction starts from zero. Institutional knowledge disappears between sessions.' },
  { icon: Plug, name: 'Integration Layer', color: 'from-cyan-400/20 to-cyan-400/5', accent: 'text-cyan-400', border: 'border-cyan-400/20',
    what: 'Connects to business systems, calendars, documents, CRM, finance tools, communications, and internal sources.',
    why: 'Agents that cannot connect to existing tools remain isolated from the operational reality of the business.' },
  { icon: ShieldCheck, name: 'Policy & Permission Layer', color: 'from-amber-400/20 to-amber-400/5', accent: 'text-amber-400', border: 'border-amber-400/20',
    what: 'Controls what agents are allowed to do, which teams can access what, and which actions are restricted or allowed.',
    why: 'Without policy controls, AI operates without boundaries — creating compliance and operational risk.' },
  { icon: UserCheck, name: 'Approval Layer', color: 'from-rose-400/20 to-rose-400/5', accent: 'text-rose-400', border: 'border-rose-400/20',
    what: 'Routes sensitive actions to the right humans for confirmation, review, sign-off, or escalation.',
    why: 'Critical decisions require human judgment. The approval layer ensures nothing bypasses governance.' },
  { icon: FileSearch, name: 'Audit Layer', color: 'from-orange-400/20 to-orange-400/5', accent: 'text-orange-400', border: 'border-orange-400/20',
    what: 'Tracks what happened, when, why, which agent acted, what changed, and what remains visible for review.',
    why: 'Without auditability, there is no accountability — and no way to demonstrate compliance.' },
  { icon: Eye, name: 'Executive Control Layer', color: 'from-aurocle-gold/20 to-aurocle-gold/5', accent: 'text-aurocle-gold', border: 'border-aurocle-gold/20',
    what: 'Gives leadership visibility into priorities, delays, approvals, prepared work, and operational flow.',
    why: 'Without executive visibility, AI becomes a black box that leadership cannot govern or trust.' },
];

export default function MultiLayerExplainer() {
  const [active, setActive] = useState(1);

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="multilayer-explainer-section">
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-15" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-aurocle-gold/4 rounded-full blur-[200px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <div className="overline mb-4">Nine Architectural Layers</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">
              What each layer does — and{' '}
              <span className="font-accent italic text-aurocle-gold">why it matters.</span>
            </h2>
            <p className="text-base text-aurocle-platinum-muted max-w-2xl mx-auto">
              Single-layer AI stops at the interface. Aurocle Agents is built across nine architectural layers — each solving a critical requirement that serious deployment demands.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Layer selector */}
          <ScrollReveal delay={100}>
            <div className="space-y-1.5 lg:sticky lg:top-24">
              {LAYER_DATA.map((layer, i) => {
                const Icon = layer.icon;
                const isActive = active === i;
                return (
                  <button key={i} onClick={() => setActive(i)} data-testid={`layer-btn-${i}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all border ${
                      isActive
                        ? `bg-gradient-to-r ${layer.color} ${layer.border} shadow-lg`
                        : 'border-white/4 hover:border-white/10'
                    }`}>
                    <Icon size={14} className={isActive ? layer.accent : 'text-aurocle-text-secondary'} />
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-heading font-medium block ${isActive ? 'text-aurocle-text' : 'text-aurocle-text-secondary'}`}>
                        {layer.name}
                      </span>
                    </div>
                    <span className="text-[9px] font-mono text-aurocle-text-secondary">0{i + 1}</span>
                  </button>
                );
              })}
            </div>
          </ScrollReveal>

          {/* Active layer detail */}
          <ScrollReveal delay={200}>
            <div className={`glass-card p-8 lg:p-10 ${LAYER_DATA[active].border} relative overflow-hidden min-h-[320px]`}
              data-testid="layer-detail">
              <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl ${LAYER_DATA[active].color} rounded-full blur-[100px] pointer-events-none`} />

              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  {(() => { const Icon = LAYER_DATA[active].icon; return <div className={`w-12 h-12 flex items-center justify-center border ${LAYER_DATA[active].border} agent-working`}><Icon size={22} className={LAYER_DATA[active].accent} /></div>; })()}
                  <div>
                    <span className="text-[10px] font-mono text-aurocle-text-secondary uppercase tracking-wider">Layer 0{active + 1} of 09</span>
                    <h3 className="font-heading text-xl font-medium text-aurocle-text">{LAYER_DATA[active].name}</h3>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs font-mono text-aurocle-gold/60 uppercase tracking-wider mb-3">What It Does</div>
                    <p className="text-sm text-aurocle-text leading-relaxed">{LAYER_DATA[active].what}</p>
                  </div>
                  <div>
                    <div className="text-xs font-mono text-red-400/60 uppercase tracking-wider mb-3">Why It Matters</div>
                    <p className="text-sm text-aurocle-platinum-muted leading-relaxed">{LAYER_DATA[active].why}</p>
                  </div>
                </div>

                {/* Architecture position */}
                <div className="mt-8 pt-6 border-t border-white/5">
                  <div className="text-[9px] font-mono text-aurocle-text-secondary uppercase tracking-wider mb-3">Position in Architecture</div>
                  <div className="flex gap-[3px]">
                    {LAYER_DATA.map((_, j) => (
                      <div key={j}
                        className={`h-3 flex-1 rounded-sm transition-all cursor-pointer ${
                          j === active ? `${LAYER_DATA[j].accent.replace('text-', 'bg-')}/60 shadow-[0_0_8px_rgba(212,175,55,0.2)]`
                          : j < active ? 'bg-white/15' : 'bg-white/5'
                        }`}
                        onClick={() => setActive(j)}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[8px] font-mono text-aurocle-text-secondary">Interface</span>
                    <span className="text-[8px] font-mono text-aurocle-text-secondary">Executive Control</span>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={300}>
          <div className="mt-12 text-center">
            <p className="text-xs text-aurocle-platinum-muted mb-6 max-w-lg mx-auto">
              Without these layers, AI remains shallow. With them, it becomes operational infrastructure for serious organizations.
            </p>
            <a href="#pricing" className="btn-gold cta-burning" data-testid="layers-to-pricing-cta">
              See Architecture-Based Pricing <ArrowRight size={14} />
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
